import {
  Component,
  computed,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ChartType, ChartOptions } from 'chart.js';
import {
  AssetAvailabilitySummary,
  GeoAvailabilityData,
  GeoAvailabilityMetric,
  GeoAvailabilityMetricIdKey,
} from 'src/app/core/models';
import { AssetAvailability, Customer } from 'src/app/core/models';
import { ComponentBase } from '../component.base';
import { GeographyStatsItem } from './geography-stats.component';

type AssetAvailabilityWithTranslation = AssetAvailability & {
  translatedLabel: string;
};

type AssetAvailabilitySummaryWithTranslation = Omit<
  AssetAvailabilitySummary,
  'availability'
> & { availability: AssetAvailabilityWithTranslation[] };

@Component({
  selector: 'app-assets',
  templateUrl: './asset-availability.component.html',
  styleUrls: ['./asset-availability.component.scss'],
  standalone: false,
})
export class AssetAvailabilityComponent
  extends ComponentBase
  implements OnInit
{
  assetAvailabilitySummary?: AssetAvailabilitySummary = undefined;
  categoryId: number;
  productTypeId: number;
  public availableAvailability: AssetAvailability | null;
  public onRentAvailability: AssetAvailability | null;
  public unavailableAvailability: AssetAvailability | null;
  public hasAssetAvailabilitySubscription = false;
  public pieChartLabels: any[] = [];
  public pieChartData: any[] = [];
  public pieChartType: ChartType = 'pie';
  public pieChartPlugins = [];
  public chartColors: Array<string> = [];
  public pieChartOptions: ChartOptions<'pie'> = {};
  public pieChartDatasets: any[] = [];
  public pieChartLegend = false;
  public showGeographyStats: WritableSignal<boolean> = signal(false);
  protected pieChartLabelMap: Map<string, string> = new Map();

  public geographyStatsData: WritableSignal<GeoAvailabilityData | null> =
    signal(null);
  labelSuffix: string = ' %';

  public geographyStatsList: Signal<GeographyStatsItem[]> = computed<
    GeographyStatsItem[]
  >(() => {
    const getIds = (key: GeoAvailabilityMetric, data: GeoAvailabilityData) => {
      if (key === 'company') {
        return [];
      }
      return data[`client_${key}_id`] || [];
    };

    const labels: Record<string, string> = {
      branch: this.translateService.instant('main.labels.Branch'),
      district: this.translateService.instant('main.labels.District'),
      region: this.translateService.instant('main.labels.Region'),
      company: this.translateService.instant('main.labels.Company'),
    };

    const data: GeoAvailabilityData | null = this.geographyStatsData();
    if (data) {
      const keys: GeoAvailabilityMetric[] = Object.keys(data).filter((key) =>
        ['branch', 'district', 'region', 'company'].includes(key)
      ) as GeoAvailabilityMetric[];
      return keys.map((key): GeographyStatsItem => {
        return {
          name: (key.charAt(0).toUpperCase() +
            key.slice(1)) as GeoAvailabilityMetric,
          label: labels[key],
          ids: getIds(key, data),
          count: data[key],
        };
      });
    }
    return [];
  });

  ngOnInit(): void {
    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    const categoryId = this.route.snapshot.paramMap.get('categoryId');
    const productTypeId = this.route.snapshot.paramMap.get('productTypeId');
    if (!categoryId || !productTypeId) {
      this.router.navigate(['/categories']);
    } else {
      this.subscriptions.push(
        this.profileService.selectedCustomersChanged.subscribe(
          (r: Array<Customer>) => {
            this.loadPageData();
          }
        )
      );
      this.categoryId = +categoryId;
      this.productTypeId = +productTypeId;
      this.loadPageData();
    }
    const showStatsDefault = this.defaultsService.getFilterDefault(
      'mobile.display.geo-level-availability'
    );
    this.showGeographyStats.set(showStatsDefault?.DefaultValue === 1);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s: Subscription) => {
      s.unsubscribe();
    });
  }

  public getLabel(label: string): string {
    const getTranslationKey = (l: string): string => {
      switch (l) {
        case 'available':
          return 'main.data.available';
        case 'unavailable':
          return 'main.data.unavailable';
        case 'on rent':
          return 'main.data.on_rent';
      }
      return '';
    };

    const key: string = getTranslationKey(label.toLowerCase());

    if (!key) {
      return label;
    }

    return this.translateService.instant(key);
  }

  public loadPageData = () => {
    super.loadPageData();
    console.log('asset-availability.component.loadPageData  ');
    const assetAvailabilitySummary =
      this.equipmentService.assetAvailabilitySummary;
    if (!assetAvailabilitySummary) {
      if (!this.hasAssetAvailabilitySubscription) {
        this.subscriptions.push(
          this.equipmentService.assetAvailabilitySummaryLoaded.subscribe(
            (avs) => {
              this.configureDataPoints(avs);
              this.authenticationService.finishSentryTransaction();
            }
          )
        );
        this.hasAssetAvailabilitySubscription = true;
      }
      this.equipmentService.getAssetAvailabilitySummary(
        this.categoryId,
        this.productTypeId
      );
    } else {
      this.configureDataPoints(assetAvailabilitySummary);
      this.authenticationService.finishSentryTransaction();
    }
  };

  public configureDataPoints = (avs?: AssetAvailabilitySummary) => {
    this.pieChartData = [];
    this.chartColors = [];
    this.pieChartLabels = [];
    if (avs) {
      const geographyStats = avs.available_counts;
      this.geographyStatsData.set(geographyStats);
      avs.availability.forEach((r) => {
        switch (r.status_type.toLowerCase()) {
          case 'available':
            this.availableAvailability =
              r.asset_percent && r.asset_percent > 0 ? { ...r } : null;
            break;
          case 'unavailable':
            this.unavailableAvailability =
              r.asset_percent && r.asset_percent > 0 ? { ...r } : null;
            break;
          case 'onrent':
            this.onRentAvailability =
              r.asset_percent && r.asset_percent > 0 ? { ...r } : null;
            break;
        }
        this.pieChartData.push(r.asset_percent);
        this.chartColors.push(r.label_color);
        const translatedLabel = this.getLabel(r.status_label);
        this.pieChartLabelMap.set(r.status_label, translatedLabel);
        this.pieChartLabels.push(
          this.translateService.instant(translatedLabel) + this.labelSuffix
        );
      });

      this.assetAvailabilitySummary = avs;
      this.isBusy = false;
    }
    this.pieChartDatasets = [
      { data: this.pieChartData, backgroundColor: [...this.chartColors] },
    ];
    this.pieChartOptions = { responsive: true, maintainAspectRatio: false };
  };

  protected getChartLabelFromTranslation(translated: string): string {
    const values = Array.from(this.pieChartLabelMap.values());
    const keys = Array.from(this.pieChartLabelMap.keys());
    const foundIndex = values.findIndex((value) => value === translated);
    return keys[foundIndex];
  }

  public chartClicked(e: any): void {
    if (e.active.length > 0) {
      const activeItem = e.active[0];
      if (activeItem) {
        let label: string =
          e.event.chart.legend.legendItems[activeItem.index].text;
        label = label.replace(this.labelSuffix, '');
        const englishLabel = this.getChartLabelFromTranslation(label);
        const assetAvailability =
          this.assetAvailabilitySummary?.availability.find(
            (r) => r.status_label === englishLabel
          );
        if (assetAvailability) {
          const untranslatedLabel = this.getChartLabelFromTranslation(label);
          console.log(
            'asset-availability.component.chartClicked  ',
            untranslatedLabel
          );

          this.openEquipmentAssetByStatus(
            assetAvailability.status_type,
            assetAvailability.status_label
          );
        }
      }
    }
  }

  openAllEquipmentAssetStatuses = (assetSummary: AssetAvailabilitySummary) => {
    this.navService.showBackButton = true;
    this.navService.backButtonRoute = '/equipment-asset';
    this.navService.backButtonRouteParams = [
      this.categoryId.toString(),
      this.productTypeId.toString(),
    ];
    this.navService.pageTitle = `${this.navService.pageTitle} ${assetSummary.asset_description}`;
    this.router.navigate([
      '/equipment-asset',
      this.categoryId,
      this.productTypeId,
    ]);
  };

  public onGeoNavigate = (type: string) => {
    // Geography stats are always for "available" assets
    const statusType = this.availableAvailability?.status_type || 'available';
    const statusLabel = this.availableAvailability?.status_label || 'Available';

    const key = type.toLowerCase() as GeoAvailabilityMetric;
    const statsData = this.geographyStatsData();

    // Get the count for this geography level
    const count = statsData?.[key] || 0;

    // Don't navigate if count is 0
    if (count === 0) {
      return;
    }

    const ids =
      statsData && statsData[`client_${key}_id` as GeoAvailabilityMetricIdKey];

    this.openEquipmentAssetByStatus(
      statusType,
      statusLabel,
      key,
      ids ? ids : undefined
    );
  };

  public openEquipmentAssetByStatus = (
    statusType: string | null,
    statusLabel: string | null,
    geographyType?: string,
    ids?: number[]
  ) => {
    this.equipmentService.equipmentAssets = undefined;
    statusType = statusType ? statusType.toLocaleLowerCase() : '';
    statusType = statusType ? statusType : '';
    const translatedStatusLabel = statusLabel ? this.getLabel(statusLabel) : '';
    if (statusType !== '') {
      this.navService.pageTitle = `${this.navService.pageTitle}<br/>${translatedStatusLabel}`;
    } else {
      this.navService.pageTitle = `${this.navService.pageTitle}<br/>${this.translateService.instant('main.data.all_equipment_assets')}`;
    }
    if (statusType.toLowerCase() === 'unavailable') {
      this.equipmentService.assetUnavailabilitySummary = undefined;
      this.router.navigate([
        '/asset-unavailability',
        this.categoryId,
        this.productTypeId,
      ]);
    } else {
      this.equipmentService.equipmentAssets = undefined;
      const options: any = {};
      if (geographyType) {
        options.queryParams = {
          geography: geographyType,
        };
      }

      if (ids && ids.length > 0) {
        options.queryParams = {
          ...options.queryParams,
          ids: ids.join(','),
        };
      }

      this.router.navigate(
        ['/equipment-asset', this.categoryId, this.productTypeId, statusType],
        options
      );
    }
  };
}
